import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import API from "../config/apiConfig";

export default function DiscountBanner({ variant = "header" }) {
  const [discounts, setDiscounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchDiscounts();
  }, []);

  const fetchDiscounts = async () => {
    try {
      setError(null);
      const url = `${API}/discounts`;
      console.log("🔄 Fetching discounts from:", url);
      console.log("📦 API Base URL:", API);
      
      const response = await axios.get(url, {
        timeout: 5000,
        headers: {
          'Accept': 'application/json',
        }
      });

      console.log("✅ API Response received:", response.data);

      let discountData = [];

      // Handle different response formats
      if (Array.isArray(response.data)) {
        discountData = response.data;
        console.log("📋 Format: Direct array");
      } else if (response.data.data && Array.isArray(response.data.data)) {
        discountData = response.data.data;
        console.log("📋 Format: .data array");
      } else if (response.data.success && response.data.data) {
        discountData = Array.isArray(response.data.data)
          ? response.data.data
          : [response.data.data];
        console.log("📋 Format: .success with data");
      } else if (response.data) {
        // If data is a single object, wrap it in array
        discountData = [response.data];
        console.log("📋 Format: Single object wrapped");
      }

      console.log("📊 Processed discount data:", discountData);
      console.log("📈 Discount count:", discountData.length);

      if (discountData && discountData.length > 0) {
        setDiscounts(discountData);
        console.log("✨ Discounts loaded successfully!");
      } else {
        console.warn("⚠️ No discounts received");
        setDiscounts([]);
      }
    } catch (error) {
      // Silently handle 404 (endpoint not implemented) - just show wellness quotes instead
      if (error.response?.status === 404) {
        console.log("ℹ️ Discounts endpoint not yet implemented (404) - using wellness quotes");
        setDiscounts([]);
      } else {
        // Log other errors
        console.error("❌ Discount fetch failed");
        console.error("Error message:", error.message);
        console.error("Error code:", error.code);
        
        if (error.response) {
          console.error("Response status:", error.response.status);
          console.error("Response data:", error.response.data);
        } else if (error.request) {
          console.error("No response received. Request details:", error.request);
        }
        
        setError(error.message);
        setDiscounts([]);
      }
    } finally {
      setLoading(false);
    }
  };

  const getWellnessQuotes = () => [
    "✨ Wellness is a journey, not a destination.",
    "🌿 Ayurveda: The science of life and longevity.",
    "💚 Every sip brings you closer to wellness.",
    "🧘 Balance your body, mind, and spirit with nature.",
    "🌱 Nature's wisdom, delivered with technology.",
    "✨ Transform your health with ancient remedies.",
    "💪 Invest in your wellness today.",
    "🌿 Pure herbs, personalized wellness solutions.",
  ];

  const getDemoDiscounts = () => [
    {
      id: 1,
      minimum_purchase_amount: 1000,
      discount_amount: 100,
      discount_percentage: 10,
      description: "Get ₹100 off for every ₹1,000 purchase!",
    },
    {
      id: 2,
      minimum_purchase_amount: 5000,
      discount_amount: 600,
      discount_percentage: 12,
      description: "Get ₹600 off for every ₹5,000 purchase!",
    },
  ];

  // Memoized message list to prevent re-renders
  const messageList = useMemo(() => {
    const quotes = getWellnessQuotes();
    const list = (discounts && discounts.length > 0)
      ? discounts.map(
          (d) =>
            `🎁 Get ₹${d.discount_amount} off for every ₹${d.minimum_purchase_amount} purchase!`
        )
      : quotes;
    return [...list, ...list, ...list];
  }, [discounts]);

  return (
    <div className="discount-banner-wrapper w-full fixed top-0 left-0 right-0 z-50 bg-primary">
      {/* Always render the banner at fixed height */}
      <div className="w-full bg-primary text-white overflow-hidden py-2 px-4 h-12 flex items-center">
        <style>{`
          @keyframes ticker-scroll {
            0% {
              transform: translateX(0%);
            }
            100% {
              transform: translateX(-50%);
            }
          }

          .discount-ticker {
            display: flex;
            width: max-content;
            animation: ticker-scroll ${messageList.length * 8}s linear infinite;
          }

          .discount-ticker:hover {
            animation-play-state: paused;
          }

          .ticker-item {
            display: flex;
            align-items: center;
            gap: 8px;
            padding: 0 40px;
            white-space: nowrap;
            font-size: 14px;
            font-weight: 500;
          }

          .loading-text {
            opacity: ${loading ? 1 : 0};
            position: absolute;
            left: 16px;
            transition: opacity 0.3s ease-out;
          }

          .discount-ticker {
            opacity: ${loading ? 0 : 1};
            transition: opacity 0.3s ease-out;
            animation: ticker-scroll ${messageList.length * 8}s linear infinite;
          }
        `}</style>

        {/* Loading state - hidden with opacity */}
        <p className="text-sm animate-pulse loading-text"></p>

        {/* Ticker content - hidden with opacity during loading */}
        <div className="flex overflow-hidden w-full">
          <div className="discount-ticker">
            {messageList.map((msg, index) => (
              <div key={index} className="ticker-item">
                {msg}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Error indicator at bottom */}
      {error && (
        <div className="text-xs text-yellow-100 px-4 py-1 bg-yellow-700/30">
          ⚠️ Showing demo discounts (API error: {error})
        </div>
      )}
    </div>
  );
}
